import { useQuery } from '@apollo/client'
import { ExclamationTriangleIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline'
import { GET_EOD_SESSION } from '../../graphql/production-queries'

interface YesterdayHandoverWidgetProps {
  yesterday: string // YYYY-MM-DD
}

const SEVERITY_STYLES: Record<string, string> = {
  high:   'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low:    'bg-blue-100 text-blue-700 border-blue-200',
}

const SEVERITY_LABELS: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const YesterdayHandoverWidget = ({ yesterday }: YesterdayHandoverWidgetProps) => {
  const { data, loading } = useQuery(GET_EOD_SESSION, {
    variables: { date: yesterday },
    fetchPolicy: 'cache-and-network',
  })

  const session = data?.eodSession ?? null
  const incidents: any[] = session?.incidents ?? []
  const notesForTomorrow: string | null = session?.notes_for_tomorrow ?? null

  if (loading) return null

  const hasIncidents = incidents.length > 0
  const hasNotes = !!notesForTomorrow?.trim()

  return (
    <div className="rounded-2xl border border-gray-100 shadow-sm bg-white p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Handover from yesterday
        </h2>
        <span className="text-xs text-gray-400">
          {new Date(yesterday + 'T00:00:00').toLocaleDateString('nl-NL', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </span>
      </div>

      {/* Notes for tomorrow (= notes for today) */}
      <div className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
        <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
            Notes for today
          </p>
          {hasNotes ? (
            <p className="text-sm text-gray-800 whitespace-pre-line">{notesForTomorrow}</p>
          ) : session ? (
            <p className="text-sm text-gray-400 italic">No notes left for today.</p>
          ) : (
            <p className="text-sm text-gray-400 italic">No end-of-shift was logged yesterday.</p>
          )}
        </div>
      </div>

      {/* Incidents — only shown if any occurred */}
      {hasIncidents && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Incidents reported ({incidents.length})
            </p>
          </div>
          <div className="space-y-2">
            {incidents.map((inc: any, idx: number) => (
              <div
                key={idx}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${SEVERITY_STYLES[inc.severity] ?? 'bg-gray-50 text-gray-700 border-gray-200'}`}
              >
                <span className="text-xs font-semibold uppercase tracking-wide mt-0.5 flex-shrink-0 w-14">
                  {SEVERITY_LABELS[inc.severity] ?? inc.severity}
                </span>
                <p className="text-sm leading-snug">{inc.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default YesterdayHandoverWidget
